import { supabase } from './auth.service';
import { Product } from '../entities/product.entity';

export interface Wishlist {
  id: string;
  userId: string;
  accountId: string;
  products: Product[];
  createdAt: string;
  updatedAt: string;
}

export class WishlistService {
  async getWishlist(): Promise<Wishlist> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: wishlist } = await supabase
      .from('wishlists')
      .select('id, account_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!wishlist) {
      return {
        id: '',
        userId: user.id,
        accountId: '',
        products: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }

    const { data, error } = await supabase
      .from('wishlist_items')
      .select('*, products(*)')
      .eq('wishlist_id', wishlist.id);

    if (error) throw error;

    return {
      id: wishlist.id,
      userId: user.id,
      accountId: wishlist.account_id || '',
      products: data?.map((item) => this.mapProductToEntity(item.products)) || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  private async getAccountId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const accountId = user.user_metadata?.account_id;
    if (accountId) return accountId;

    const { data } = await supabase.from('users').select('account_id').eq('id', user.id).single();
    return data?.account_id || '';
  }

  async addToWishlist(productId: string): Promise<Wishlist> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let { data: wishlist } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!wishlist) {
      const accountId = await this.getAccountId();
      const { data: newWishlist, error } = await supabase
        .from('wishlists')
        .insert({ user_id: user.id, account_id: accountId, name: 'My Wishlist' })
        .select('*')
        .single();

      if (error) throw error;
      wishlist = newWishlist;
    }

    const { error } = await supabase.from('wishlist_items').insert({
      wishlist_id: wishlist.id,
      product_id: productId,
    });

    if (error) throw error;

    return this.getWishlist();
  }

  async removeFromWishlist(productId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: wishlist } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!wishlist) return;

    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('wishlist_id', wishlist.id)
      .eq('product_id', productId);

    if (error) throw error;
  }

  async clearWishlist(): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: wishlist } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!wishlist) return;

    const { error } = await supabase.from('wishlist_items').delete().eq('wishlist_id', wishlist.id);

    if (error) throw error;
  }

  private mapProductToEntity(row: any): Product {
    return {
      id: row.id,
      accountId: row.account_id,
      label: row.label,
      description: row.description,
      slug: row.slug,
      categoryId: row.category_id,
      brandId: row.brand_id,
      tags: row.tags || [],
      media: row.media_data || [],
      variants:
        row.product_variants?.map((v: any) => ({
          id: v.id,
          productId: row.id,
          accountId: row.account_id,
          label: v.label,
          sku: v.sku,
          barcode: v.barcode,
          stock: v.stock,
          weight: v.weight,
          requiresShipping: v.requires_shipping,
          isDefault: v.is_default,
          options: v.options,
          media: v.media_data || null,
          createdAt: v.created_at || '',
          updatedAt: v.updated_at || '',
        })) || [],
      price: row.price,
      compareAtPrice: row.compare_at_price,
      costPerItem: row.cost_per_item,
      status: row.status,
      isVisible: row.is_visible,
      metaTitle: row.meta_title,
      metaDescription: row.meta_description,
      customFields: row.custom_fields,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}

export const wishlistService = new WishlistService();
